/** Thin client for the internal /api: JSON in and out, RFC 9457 problems become ApiError. */
import { QueryClient, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from './toast';

export interface FieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public errors?: FieldError[] | unknown,
  ) {
    super(message);
  }
  get fieldErrors(): FieldError[] {
    return Array.isArray(this.errors) ? (this.errors as FieldError[]).filter((e) => e && typeof e.path === 'string') : [];
  }
}

export const UNAUTHORIZED_EVENT = 'raremargin:unauthorized';

/** The company is read-only until someone subscribes; `api` already showed a toast linking to Billing. */
export const isPaywall = (err: unknown) => err instanceof ApiError && err.status === 402 && err.code === 'subscription_required';

export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const res = await fetch(path.startsWith('/') ? path : `/api/${path}`, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const p = (data ?? {}) as { code?: string; detail?: string; title?: string; errors?: unknown };
    if (res.status === 401 && !path.includes('/auth/')) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    const err = new ApiError(res.status, p.code ?? 'error', p.detail ?? p.title ?? `Request failed (${res.status})`, p.errors);
    if (isPaywall(err)) toast.paywall(err.message);
    throw err;
  }
  return data as T;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

/** GET an /api path. Pass `null` to skip. */
export function useApi<T>(path: string | null, opts: Partial<UseQueryOptions<T, ApiError>> = {}) {
  return useQuery<T, ApiError>({
    queryKey: [path],
    queryFn: ({ signal }) => api<T>(path!, { signal }),
    enabled: path !== null && (opts.enabled ?? true),
    ...opts,
  });
}

/**
 * A write. On success every cached query is refreshed: postings ripple through balances, reports, controls and the
 * command center, so partial invalidation would show stale numbers.
 */
export function useAction<I = void, O = unknown>(fn: (input: I) => Promise<O>, opts: { success?: string | ((o: O) => string); silent?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation<O, ApiError, I>({
    mutationFn: fn,
    onSuccess: async (o) => {
      await qc.invalidateQueries();
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(o) : opts.success);
    },
    onError: (err) => {
      if (!opts.silent && !isPaywall(err)) toast.error(err.message);
    },
  });
}

export function qs(params: Record<string, string | number | boolean | null | undefined>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Server-sent events over a POST body (EventSource only does GET). */
export async function streamSse(path: string, body: unknown, onEvent: (event: string, data: unknown) => void, signal?: AbortSignal) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
    signal,
  });
  if (!res.ok || !res.body) {
    const p = (await res.json().catch(() => ({}))) as { code?: string; detail?: string };
    const err = new ApiError(res.status, p.code ?? 'error', p.detail ?? `Request failed (${res.status})`);
    if (isPaywall(err)) toast.paywall(err.message);
    throw err;
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      let event = 'message';
      const data: string[] = [];
      for (const line of chunk.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
      }
      if (!data.length) continue;
      try {
        onEvent(event, JSON.parse(data.join('\n')));
      } catch {
        onEvent(event, data.join('\n'));
      }
    }
  }
}
